import React, { useState } from 'react';
import { ChefHat, History, Check, MessageSquare, Plus } from 'lucide-react';
export type MealOption = {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  prepTime: string;
  difficulty: string;
};
type OptionSet = {
  id: string;
  options: MealOption[];
  agentMessage: string;
  timestamp: Date;
};
interface MealOptionsDisplayProps {
  currentOptions: MealOption[] | null;
  agentMessage: string | null;
  isLoading: boolean;
  onSelectOption: (optionId: string) => void;
  selectedOptionId: string | null;
  onGenerateRecipe: () => void;
  onGenerateMoreOptions: () => void;
  optionHistory: OptionSet[];
  onViewHistory: (setId: string) => void;
}
export function MealOptionsDisplay({
  currentOptions,
  agentMessage,
  isLoading,
  onSelectOption,
  selectedOptionId,
  onGenerateRecipe,
  onGenerateMoreOptions,
  optionHistory,
  onViewHistory
}: MealOptionsDisplayProps) {
  const [showHistory, setShowHistory] = useState(false);
  if (isLoading) {
    return <div className="w-full border-2 border-dashed border-gray-300 flex flex-col items-center justify-center p-8 bg-white/50 animate-pulse min-h-[300px]" data-id="element-105">
        <div className="font-mono text-gray-400 text-center space-y-2" data-id="element-106">
          <p className="uppercase tracking-widest" data-id="element-107">Analyzing Preferences</p>
          <p className="text-xs" data-id="element-108">Evaluating dietary requirements...</p>
          <p className="text-xs" data-id="element-109">Generating meal concepts...</p>
          <p className="text-xs" data-id="element-110">Optimizing for group preferences...</p>
        </div>
      </div>;
  }
  if (!currentOptions) {
    return <div className="w-full border-2 border-dashed border-gray-200 flex flex-col items-center justify-center p-8 bg-gray-50/50 min-h-[300px]" data-id="element-111">
        <div className="text-center space-y-4 max-w-xs" data-id="element-112">
          <div className="w-16 h-16 border-2 border-gray-300 rounded-full flex items-center justify-center mx-auto text-gray-300" data-id="element-113">
            <ChefHat className="w-8 h-8" data-id="element-114" />
          </div>
          <p className="font-mono text-gray-400 text-sm uppercase tracking-widest" data-id="element-115">
            Awaiting Generation Request
          </p>
        </div>
      </div>;
  }
  return <div className="w-full border-2 border-gray-800 bg-white" data-id="element-116">
      {/* Agent Message Section */}
      {agentMessage && <div className="border-b-2 border-gray-200 p-4 bg-gray-50" data-id="element-117">
          <div className="flex gap-2" data-id="element-118">
            <div className="w-6 h-6 flex-shrink-0 border border-gray-300 bg-white flex items-center justify-center" data-id="element-119">
              <MessageSquare className="w-3 h-3 text-gray-400" data-id="element-120" />
            </div>
            <div className="flex-1 bg-white border border-gray-200 p-3" data-id="element-121">
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-122">
                {agentMessage}
              </p>
            </div>
          </div>
        </div>}

      {/* Meal Options - Scrollable on desktop, natural flow on mobile */}
      <div className="max-h-[400px] lg:max-h-[500px] overflow-y-auto p-4" data-id="element-123">
        <div className="flex items-center justify-between mb-3" data-id="element-124">
          <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-125">
            Meal Concepts ({currentOptions?.length || 0})
          </h3>
          <div className="flex items-center space-x-2" data-id="element-126">
            {optionHistory.length > 1 && <button onClick={() => setShowHistory(!showHistory)} className="flex items-center space-x-1 text-xs font-mono text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1" data-id="element-127">
                <History className="w-3 h-3" data-id="element-128" />
                <span data-id="element-129">
                  {showHistory ? 'Hide' : 'History'} ({optionHistory.length - 1}
                  )
                </span>
              </button>}
          </div>
        </div>

        {showHistory && optionHistory.length > 1 && <div className="mb-4 p-3 border-2 border-dashed border-gray-200 bg-gray-50 space-y-2" data-id="element-130">
            <p className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-2" data-id="element-131">
              Previous Sets
            </p>
            {optionHistory.slice(0, -1).reverse().map(set => <button key={set.id} onClick={() => onViewHistory(set.id)} className="w-full text-left p-2 border border-gray-300 hover:border-gray-500 bg-white transition-colors" data-id="element-132">
                  <div className="flex items-center justify-between" data-id="element-133">
                    <span className="text-xs font-mono text-gray-700" data-id="element-134">
                      {set.options.length} options
                    </span>
                    <span className="text-xs font-mono text-gray-400" data-id="element-135">
                      {set.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                </button>)}
          </div>}

        <div className="space-y-2" data-id="element-136">
          {currentOptions?.map(option => <button key={option.id} onClick={() => onSelectOption(option.id)} className={`w-full text-left border transition-all hover:border-gray-500 ${selectedOptionId === option.id ? 'border-gray-900 bg-gray-50' : 'border-gray-300 bg-white'}`} data-id="element-137">
              <div className="flex gap-3 p-3" data-id="element-138">
                <div className="w-12 h-12 flex-shrink-0 border border-gray-200 bg-gray-100 flex items-center justify-center text-2xl" data-id="element-139">
                  {option.thumbnail}
                </div>
                <div className="flex-1 min-w-0" data-id="element-140">
                  <div className="flex items-start justify-between gap-2 mb-1" data-id="element-141">
                    <h4 className="font-bold text-sm text-gray-900 leading-tight" data-id="element-142">
                      {option.title}
                    </h4>
                    {selectedOptionId === option.id && <div className="flex-shrink-0 w-4 h-4 bg-gray-900 flex items-center justify-center" data-id="element-143">
                        <Check className="w-3 h-3 text-white" data-id="element-144" />
                      </div>}
                  </div>
                  <p className="text-xs text-gray-600 leading-snug line-clamp-2 mb-1" data-id="element-145">
                    {option.description}
                  </p>
                  <div className="flex items-center space-x-3 text-xs font-mono text-gray-400" data-id="element-146">
                    <span data-id="element-147">{option.prepTime}</span>
                    <span data-id="element-148">•</span>
                    <span data-id="element-149">{option.difficulty}</span>
                  </div>
                </div>
              </div>
            </button>)}
        </div>
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="border-t-2 border-gray-200 p-4 bg-gray-50" data-id="element-150">
        <div className="flex gap-2" data-id="element-151">
          <button onClick={onGenerateMoreOptions} disabled={isLoading} className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 border-2 border-gray-300 hover:border-gray-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-152">
            <Plus className="w-3 h-3" data-id="element-153" />
            <span className="font-mono text-xs uppercase tracking-widest" data-id="element-154">
              More Options
            </span>
          </button>

          {selectedOptionId && <button onClick={onGenerateRecipe} disabled={isLoading} className="flex-1 px-4 py-3 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-mono text-xs uppercase tracking-widest" data-id="element-155">
              Generate Recipe
            </button>}
        </div>
      </div>
    </div>;
}